import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry error tracking.
 * Only active in production when VITE_SENTRY_DSN is set.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    if (import.meta.env.DEV) {
      console.log('[Sentry] No DSN configured, skipping initialization')
    }
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: `neuro-ha-ui@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text and block all media for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Tracing sample rate (adjust based on traffic)
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay sample rate
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Network errors that aren't our fault
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // ResizeObserver loop errors (benign)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // Don't send PII
    beforeSend(event) {
      // Strip email from user context if present
      if (event.user?.email) {
        event.user.email = '[REDACTED]'
      }
      return event
    },
  })
}

/**
 * Capture an exception with optional context.
 */
export function captureException(error: Error, context?: Record<string, unknown>) {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.error('[Sentry disabled] Error:', error, context)
    return
  }

  Sentry.captureException(error, {
    extra: context,
  })
}

/**
 * Set user context for error tracking.
 */
export function setUser(user: { id: string; username: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      username: user.username,
    })
  } else {
    Sentry.setUser(null)
  }
}

/**
 * Add breadcrumb for debugging.
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

export { Sentry }
